import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const emptyUploadForm = { academic_year: "", fee_type_id: "", fee_head_id: "" };

const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getValue = (row, keys) => {
  const normalized = Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [normalizeKey(key), value])
  );
  for (const key of keys) {
    const value = normalized[normalizeKey(key)];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

export default function FeeConcessionUpload() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const userEmail = user?.user_email || "";
  const [feeHeads, setFeeHeads] = useState([]);
  const [form, setForm] = useState(emptyUploadForm);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    if (!clientId || !collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}`)
      .then(({ data }) =>
        setFeeHeads(Array.isArray(data?.data) ? data.data : data?.data?.items || [])
      )
      .catch((error) => {
        setFeeHeads([]);
        showSnackbar(error.response?.data?.message || "Unable to load fee heads", "error");
      });
  }, [API_BASE_URL, clientId, collegeId]);

  const academicYears = useMemo(
    () => [...new Set(feeHeads.map((head) => head.academic_year).filter(Boolean))].sort().reverse(),
    [feeHeads]
  );
  const feeTypes = useMemo(() => {
    const values = new Map();
    feeHeads
      .filter((head) => head.academic_year === form.academic_year)
      .forEach((head) =>
        values.set(String(head.fee_type_id), {
          id: String(head.fee_type_id),
          name: head.feeType?.fee_type_name || `Fee Type ${head.fee_type_id}`,
        })
      );
    return [...values.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [feeHeads, form.academic_year]);
  const availableFeeHeads = useMemo(
    () =>
      feeHeads.filter(
        (head) =>
          head.academic_year === form.academic_year &&
          String(head.fee_type_id) === String(form.fee_type_id) &&
          String(head.workflow_status).toUpperCase() === "APPROVED"
      ),
    [feeHeads, form]
  );
  const totalConcession = useMemo(
    () => rows.reduce((total, row) => total + Number(row.concession_amount || 0), 0),
    [rows]
  );

  const selectionPayload = () => ({
    academic_year: form.academic_year,
    fee_type_id: form.fee_type_id,
    fee_head_id: form.fee_head_id,
  });

  const resetPreview = () => {
    setRows([]);
    setFileName("");
    setCompleted(false);
    setResult(null);
  };

  const previewFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!form.academic_year || !form.fee_type_id || !form.fee_head_id) {
      showSnackbar("Select Academic Year, Fee Type and Fee Head first", "warning");
      event.target.value = "";
      return;
    }
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!worksheet) throw new Error("The selected file has no worksheet");
      const sourceRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      if (!sourceRows.length) throw new Error("The selected file has no data rows");
      const uploadRows = sourceRows.map((row) => ({
        student_rollno: String(
          getValue(row, ["student_rollno", "student rollno", "rollno", "roll no"])
        ).trim(),
        concession_amount: getValue(row, ["concession_amount", "concession amount", "amount"]),
        remarks: String(getValue(row, ["remarks", "remark"])).trim(),
      }));
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/bulk-upload/preview`,
        { ...selectionPayload(), rows: uploadRows }
      );
      setRows(data?.data || []);
      setFileName(file.name);
      setCompleted(false);
      setResult(null);
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Unable to read the file";
      setRows([]);
      setFileName("");
      setResult({ severity: "error", message });
      showSnackbar(message, "error");
    } finally {
      event.target.value = "";
    }
  };

  const uploadConcessions = async () => {
    if (!rows.length || rows.some((row) => row.validation)) return;
    setUploading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/bulk-upload`,
        { ...selectionPayload(), rows, created_by: userEmail }
      );
      const message = `${data.message} All displayed rows are ready in Concession Approvals.`;
      setRows((current) =>
        current.map((row) => ({ ...row, upload_status: "APPROVED - Uploaded" }))
      );
      setCompleted(true);
      setResult({ severity: "success", message });
      showSnackbar(data.message);
    } catch (error) {
      const serverRows = error.response?.data?.data;
      if (Array.isArray(serverRows)) setRows(serverRows);
      const message = error.response?.data?.message || "Concession upload failed";
      setResult({ severity: "error", message });
      showSnackbar(message, "error");
    } finally {
      setUploading(false);
    }
  };

  const clearUpload = () => {
    if (uploading) return;
    setForm(emptyUploadForm);
    resetPreview();
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ["ROLLNO", "CONCESSION_AMOUNT", "REMARKS"],
      ["240401509001", 5000, "Merit Scholarship"],
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Concession Upload");
    XLSX.writeFile(workbook, "fee-concession-upload-template.xlsx");
  };

  const columns = [
    { field: "id", headerName: "S. No.", width: 75 },
    { field: "student_rollno", headerName: "Roll No", minWidth: 140, flex: 1 },
    {
      field: "fee_amount",
      headerName: "Fee Amount",
      minWidth: 130,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    {
      field: "concession_amount",
      headerName: "Concession Amount",
      minWidth: 165,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    { field: "remarks", headerName: "Remarks", minWidth: 180, flex: 1.2 },
    {
      field: "upload_status",
      headerName: "Upload Status",
      minWidth: 160,
      valueGetter: (params) => params.row.upload_status || "Not uploaded",
    },
    {
      field: "validation",
      headerName: "Validation",
      minWidth: 250,
      flex: 1.4,
      renderCell: ({ value }) => (
        <Typography variant="caption" color={value ? "error" : "success.main"}>
          {value || "Ready"}
        </Typography>
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar sx={{ px: { xs: 0, sm: 1 } }}>
          <Typography variant="h5" fontWeight="bold">
            UPLOAD FEE CONCESSION
          </Typography>
        </Toolbar>
        <Box px={{ xs: 0, sm: 1 }} pb={1}>
          <Stack spacing={2} mt={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <TextField
                required
                select
                label="Academic Year"
                value={form.academic_year}
                disabled={completed}
                onChange={(event) => {
                  setForm({ academic_year: event.target.value, fee_type_id: "", fee_head_id: "" });
                  resetPreview();
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </TextField>
              <TextField
                required
                select
                label="Fee Type"
                value={form.fee_type_id}
                disabled={completed || !form.academic_year}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    fee_type_id: event.target.value,
                    fee_head_id: "",
                  }));
                  resetPreview();
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Fee Type</option>
                {feeTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </TextField>
              <TextField
                required
                select
                label="Fee Head"
                value={form.fee_head_id}
                disabled={completed || !form.fee_type_id}
                onChange={(event) => {
                  setForm((current) => ({ ...current, fee_head_id: event.target.value }));
                  resetPreview();
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
              >
                <option value="">Select Approved Fee Head</option>
                {availableFeeHeads.map((head) => (
                  <option key={head.fee_head_id} value={head.fee_head_id}>
                    {head.fee_head_name}
                  </option>
                ))}
              </TextField>
            </Box>
            {form.fee_type_id && !availableFeeHeads.length && (
              <Alert severity="info">
                No approved Fee Heads are available for this Academic Year and Fee Type.
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ bgcolor: "#2e7d32", color: "#fff", "&:hover": { bgcolor: "#1b5e20" } }}
              >
                Download Template
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={completed || !form.fee_head_id}
                sx={{
                  color: "#1976d2",
                  borderColor: "#1976d2",
                  "&.Mui-disabled": { borderColor: "#b0bec5", color: "#90a4ae" },
                }}
              >
                Select CSV / Excel
                <input hidden type="file" accept=".csv,.xlsx" onChange={previewFile} />
              </Button>
              <Typography variant="body2" color={fileName ? "success.main" : "text.secondary"}>
                {fileName ? `Selected: ${fileName}` : "No file selected"}
              </Typography>
            </Box>
            {result && <Alert severity={result.severity}>{result.message}</Alert>}
            {rows.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    p: 1.5,
                    bgcolor: "#f5f9ff",
                    borderRadius: 1,
                  }}
                >
                  <Typography fontWeight="bold">Rows: {rows.length}</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Total Concession Amount: ₹
                    {totalConcession.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <Box sx={{ height: 360, width: "100%" }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    pagination
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    disableSelectionOnClick
                  />
                </Box>
                {rows.some((row) => row.validation) && (
                  <Alert severity="warning">
                    Upload is disabled because one or more rows have validation errors. Correct the
                    file and select it again.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 1, pt: 1 }}>
          <Button
            variant="contained"
            onClick={clearUpload}
            disabled={uploading}
            sx={{
              bgcolor: "#d32f2f",
              color: "#fff",
              minWidth: 100,
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={uploadConcessions}
            disabled={uploading || completed || !rows.length || rows.some((row) => row.validation)}
            sx={{ color: "#fff", minWidth: 180 }}
          >
            Upload Concession
          </Button>
        </Box>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
